<script lang="ts" setup>
import type { PreviewItemData } from '@autobid/strapi-integration/typescript/auction/carPreview'
import type { DetailsCarItemData } from '@autobid/ui/types/Car'
import type { DetailsCarItemDetails } from '@autobid/ui/types/components/CarDetails'
import { useI18n } from '#imports'
import { useAutobidAuth } from '@autobid/nuxt-auth/src/composables/useAutobidAuth'
import { usePdfStore } from '@autobid/nuxt-pinia-store/store/usePdfStore'
import { getError } from '@autobid/strapi-integration/utils/getError.js'
import { useCarPageInfo } from '@autobid/ui/composables/car/useCarPageInfo'
import { attemptToAutoOpenPdf } from '@autobid/ui/utils/attemptToAutoOpenPdf'
import { useMutation } from '@tanstack/vue-query'
import { push } from 'notivue'

interface Props {
  car: DetailsCarItemData | PreviewItemData
  information: DetailsCarItemDetails['information']
  pdfSlug?: string
}

const props = defineProps<Props>()

const { locale, t } = useI18n()
const { isAuthed } = useAutobidAuth()
const { showPdfDialog } = usePdfStore()
const { pageType } = useCarPageInfo()
const { $trpc } = useNuxtApp()
const route = useRoute()

function fetchPdf() {
  if (pageType.value === 'details') {
    return $trpc.pdf.generateCarPdfById.mutate({ carId: props.car.id, lang: locale.value })
  }

  return $trpc.pdf.generateCarPdfByHash.mutate({ hash: String(route.params.slug), lang: locale.value })
}

const {
  data,
  isLoading,
  mutateAsync: generatePDF,
} = useMutation({
  mutationKey: ['generate-details-pdf', props.car.id, locale],
  mutationFn: async () => {
    // intentionally handle it inside mutation to allow unmounting of the component while pdf is generating in the background
    const toastPromise = push.promise(t('pdf-toast.processing'))
    async function inner(attempt = 0) {
      const result = await fetchPdf()

      if (result.status === 'ready') {
        toastPromise.success(t('pdf-toast.success'))
        return result
      }
      else if (attempt < 10) {
        await new Promise(resolve => setTimeout(resolve, 5_000))
        return inner(attempt + 1)
      }
      else {
        toastPromise.error(t('pdf-toast.error'))
        throw new Error('PDF generation timed out')
      }
    }

    try {
      const result = await inner()
      const { result: attemptResult } = await attemptToAutoOpenPdf(result.url)

      if (attemptResult === 'failure') {
        showPdfDialog(`details-${props.car.id}`, result.url)
      }

      return result
    }
    catch (error) {
      const { message, handled } = getError(error)

      if (!handled) {
        toastPromise.error(
          message ?? t('pdf-toast.error'),
        )
      }
    }
  },
})
</script>

<template>
  <SectionsDetailsElementsSection
    :heading-class="pdfSlug && isAuthed ? 'mr-6' : ''"
  >
    <template #heading>
      {{ $t('car-details.section.information') }}
    </template>

    <template #after-heading>
      <elements-button
        v-if="isAuthed"
        class="!w-auto !px-2"
        rounded="primary"
        :disabled="isLoading"
        @click="
          data?.url
            ? showPdfDialog(`details-${props.car.id}`, data?.url!)
            : generatePDF()
        "
      >
        <Icon mode="svg" name="uiw:printer" class="text-3xl" />
      </elements-button>
    </template>

    <template #content>
      <SectionsDetailsElementsParameters :parameters="information" />
    </template>
  </SectionsDetailsElementsSection>
</template>
